import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styles from './PdfViewer.module.css';

/* ------------------------------------------------------------------------
   PDF.js worker configuration.
   Using the CDN-hosted worker avoids Webpack/Vite worker bundling issues
   and works out-of-the-box in any React setup (CRA, Vite, Next.js, etc.).
   ------------------------------------------------------------------------ */
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * Reusable PDF Viewer Component
 *
 * A UI-focused, fully reusable PDF viewer. External logic (fetching,
 * auth, validation) should be handled by the parent.
 *
 * Props:
 * @param {string|File|Blob|ArrayBuffer|object} file  - PDF source (URL, File, Blob, or { url, data, ... }).
 * @param {number}  fromPage              - First page to render (1-indexed). If omitted with toPage, shows all pages.
 * @param {number}  toPage                - Last page to render (inclusive, 1-indexed).
 * @param {boolean} showSidePreview       - Show thumbnail sidebar on the left (default false).
 * @param {boolean} enableFullscreen      - Show fullscreen toggle button (default true).
 * @param {string}  fallbackMessage       - Message shown when no file is provided or load fails.
 * @param {string}  loadingMessage        - Message shown under the spinner while loading.
 * @param {number}  width                 - Optional fixed width (px) for main pages. Defaults to container width.
 * @param {number}  thumbnailWidth        - Width in px for sidebar thumbnails (default 120).
 * @param {string}  className             - Extra class for the outer wrapper.
 * @param {object}  style                 - Extra inline styles for the outer wrapper.
 * @param {function} onLoadSuccess        - Optional callback(pdf) when document loads.
 * @param {function} onLoadError          - Optional callback(error) when document fails to load.
 */
const PdfViewer = ({
  file,
  fromPage,
  toPage,
  showSidePreview = false,
  enableFullscreen = true,
  fallbackMessage = 'PDF is not available.',
  loadingMessage = 'Loading PDF...',
  width,
  thumbnailWidth = 120,
  className = '',
  style = {},
  onLoadSuccess,
  onLoadError,
}) => {
  const containerRef = useRef(null);
  const mainScrollRef = useRef(null);

  const [numPages, setNumPages] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activePage, setActivePage] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  /* ---------------------------------------------------------------------- */
  /*  Memoize the file prop — react-pdf re-fetches if the reference changes */
  /* ---------------------------------------------------------------------- */
  const memoizedFile = useMemo(() => file, [file]);

  const documentOptions = useMemo(
    () => ({
      cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    []
  );

  /* ---------------------------------------------------------------------- */
  /*  Responsive width tracking                                             */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const update = () => {
      if (!containerRef.current) return;
      const el = containerRef.current.querySelector(`.${styles.mainArea}`);
      if (el) setContainerWidth(el.clientWidth - 32); // minus padding
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [isFullscreen, showSidePreview]);

  /* ---------------------------------------------------------------------- */
  /*  Fullscreen handling (native Fullscreen API)                           */
  /* ---------------------------------------------------------------------- */
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      (el.requestFullscreen?.() || Promise.resolve()).catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  /* ---------------------------------------------------------------------- */
  /*  Document lifecycle callbacks                                          */
  /* ---------------------------------------------------------------------- */
  const handleLoadSuccess = useCallback(
    (pdf) => {
      setNumPages(pdf.numPages);
      setLoaded(true);
      setLoadError(false);
      if (typeof onLoadSuccess === 'function') onLoadSuccess(pdf);
    },
    [onLoadSuccess]
  );

  const handleLoadError = useCallback(
    (error) => {
      setLoaded(false);
      setLoadError(true);
      if (typeof onLoadError === 'function') onLoadError(error);
    },
    [onLoadError]
  );

  /* ---------------------------------------------------------------------- */
  /*  Compute the list of pages to render based on fromPage / toPage        */
  /* ---------------------------------------------------------------------- */
  const pagesToRender = useMemo(() => {
    if (!numPages) return [];
    const start = Math.max(1, fromPage || 1);
    const end = Math.min(numPages, toPage || numPages);
    if (start > end) return [];
    const arr = [];
    for (let i = start; i <= end; i += 1) arr.push(i);
    return arr;
  }, [numPages, fromPage, toPage]);

  /* ---------------------------------------------------------------------- */
  /*  Thumbnail click → scroll main area to the page                        */
  /* ---------------------------------------------------------------------- */
  const scrollToPage = useCallback((pageNumber) => {
    const target = document.getElementById(`pdf-page-${pageNumber}`);
    if (target && mainScrollRef.current) {
      mainScrollRef.current.scrollTo({
        top: target.offsetTop - mainScrollRef.current.offsetTop - 8,
        behavior: 'smooth',
      });
      setActivePage(pageNumber);
    }
  }, []);

  /* ---------------------------------------------------------------------- */
  /*  Reusable shared pieces                                                */
  /* ---------------------------------------------------------------------- */
  const Loader = (
    <div className={styles.loader}>
      <div className={styles.spinner} aria-hidden="true" />
      <span className={styles.loaderText}>{loadingMessage}</span>
    </div>
  );

  const Fallback = (
    <div className={styles.fallback}>
      <svg
        className={styles.fallbackIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
      <p>{fallbackMessage}</p>
    </div>
  );

  /* ---------------------------------------------------------------------- */
  /*  Render                                                                */
  /* ---------------------------------------------------------------------- */
  // No file provided → show fallback immediately
  if (!memoizedFile) {
    return (
      <div
        ref={containerRef}
        className={`${styles.wrapper} ${className}`}
        style={style}
      >
        {Fallback}
      </div>
    );
  }

  const pageWidth = width || containerWidth || undefined;

  return (
    <div
      ref={containerRef}
      className={`${styles.wrapper} ${isFullscreen ? styles.fullscreen : ''} ${className}`}
      style={style}
    >
      {/* Top toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {loaded && numPages && (
            <span className={styles.pageInfo}>
              {pagesToRender.length > 0
                ? `Showing ${pagesToRender[0]}–${pagesToRender[pagesToRender.length - 1]} of ${numPages}`
                : `${numPages} pages`}
            </span>
          )}
        </div>
        <div className={styles.toolbarRight}>
          {enableFullscreen && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 7V3h4M21 7V3h-4M3 17v4h4M21 17v4h-4" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Body: optional sidebar + main area */}
      <div className={styles.body}>
        <Document
          file={memoizedFile}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          loading={null}  /* We render our own loader */
          error={null}    /* We render our own fallback */
          noData={null}
          options={documentOptions}
          className={styles.document}
        >
          {/* Side thumbnail preview */}
          {showSidePreview && loaded && !loadError && (
            <aside
              className={styles.sidebar}
              style={{ width: thumbnailWidth + 32 }}
              aria-label="Page thumbnails"
            >
              {pagesToRender.map((pageNumber) => (
                <button
                  key={`thumb-${pageNumber}`}
                  type="button"
                  className={`${styles.thumbItem} ${
                    activePage === pageNumber ? styles.thumbItemActive : ''
                  }`}
                  onClick={() => scrollToPage(pageNumber)}
                  aria-label={`Go to page ${pageNumber}`}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={thumbnailWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading=""
                  />
                  <span className={styles.thumbLabel}>Page {pageNumber}</span>
                </button>
              ))}
            </aside>
          )}

          {/* Main viewing area */}
          <main ref={mainScrollRef} className={styles.mainArea}>
            {/* Loader overlay until document is ready */}
            {!loaded && !loadError && Loader}

            {/* Error / no-file fallback */}
            {loadError && Fallback}

            {/* Rendered pages */}
            {loaded && !loadError && pagesToRender.length === 0 && (
              <div className={styles.fallback}>
                <p>No pages to display for the given range.</p>
              </div>
            )}

            {loaded && !loadError && pagesToRender.length > 0 && (
              <div className={styles.pagesList}>
                {pagesToRender.map((pageNumber) => (
                  <div
                    key={`page-${pageNumber}`}
                    id={`pdf-page-${pageNumber}`}
                    className={styles.pageWrapper}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={pageWidth}
                      renderTextLayer
                      renderAnnotationLayer
                      loading={
                        <div className={styles.pagePlaceholder}>
                          <div className={styles.spinnerSmall} />
                        </div>
                      }
                    />
                    <div className={styles.pageNumberBadge}>Page {pageNumber}</div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;





// Basic — full PDF from a URL
// <PdfViewer file="/docs/sample.pdf" />

// Specific page range with thumbnail sidebar
// <PdfViewer
//   file="/docs/report.pdf"
//   fromPage={3}
//   toPage={8}
//   showSidePreview
// />

// From a File object (e.g. upload) with custom fallback
// <PdfViewer
//   file={uploadedFile}
//   fallbackMessage="No document uploaded yet."
//   enableFullscreen
// />

// Fully disabled sidebar + fullscreen
// <PdfViewer
//   file={pdfUrl}
//   showSidePreview={false}
//   enableFullscreen={false}
// />